import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import useActivity from "@/hooks/useActivity";
import useSeo from "@/hooks/useSeo";
import { useApp } from "@/context/AppContext";
import useStorageSync from "@/hooks/useStoredState";
import {
  getChecklist,
  getVisited,
  markVisited,
} from "@/lib/storage";
import ActivityShell from "@/components/activity/ActivityShell";
import Checklist from "@/components/activity/Checklist";
import Callout from "@/components/activity/Callout";
import StepCard from "@/components/activity/StepCard";
import SetupDiagram from "@/components/activity/SetupDiagram";
import PromotionTemplates from "@/components/activity/PromotionTemplates";
import { LoadingState, ErrorState, NotFoundState } from "@/components/common/States";

function isPhaseDone(slug, mode, phase, checklistState, visitedState) {
  if (phase.checklist && phase.checklist.length > 0) {
    return phase.checklist.every((_, i) => checklistState[phase.key]?.[i]);
  }
  return !!visitedState[phase.key];
}

export default function PhaseGuide({ mode }) {
  const { slug } = useParams();
  const { activity, status } = useActivity(slug);
  const { role, setRole } = useApp();

  const modeData = activity?.modes?.[mode];
  const phases = modeData?.phases || [];
  const [activeKey, setActiveKey] = useState(null);

  useSeo(
    modeData ? modeData.name : "Organizer Guide",
    modeData ? `${modeData.name}: a step-by-step Organizer guide to set up, run, and wrap up your event.` : "Organizer guide."
  );

  useEffect(() => {
    if (role !== "organizer") setRole("organizer");
  }, [role, setRole]);

  // Default to first phase once loaded.
  useEffect(() => {
    if (phases.length && !activeKey) setActiveKey(phases[0].key);
  }, [phases, activeKey]);

  // Track completion reactively.
  const [checklistState] = useStorageSync(() => {
    const out = {};
    phases.forEach((p) => (out[p.key] = getChecklist(slug, mode, p.key)));
    return out;
  }, [slug, mode, phases.length]);
  const [visitedState] = useStorageSync(() => getVisited(slug, mode), [slug, mode]);

  // Mark visited when a phase is opened.
  useEffect(() => {
    if (activeKey) markVisited(slug, mode, activeKey);
  }, [activeKey, slug, mode]);

  if (status === "loading") return <LoadingState />;
  if (status === "notfound") return <NotFoundState />;
  if (status === "error" || !activity) return <ErrorState onRetry={() => window.location.reload()} />;
  if (!modeData) return <NotFoundState />;

  const active = phases.find((p) => p.key === activeKey) || phases[0];

  const nav = phases.map((p) => ({
    key: p.key,
    label: p.name,
    icon: p.icon,
    done: isPhaseDone(slug, mode, p, checklistState, visitedState),
  }));

  return (
    <ActivityShell
      title={activity.name}
      eyebrow={modeData.name}
      backTo={`/activities/${slug}/organizer`}
      backLabel="Organizer Guide"
      nav={nav}
      activeKey={active?.key}
      onSelect={setActiveKey}
    >
      <div key={active.key} className="animate-fade-up space-y-5">
        <div>
          <h2 className="display-title text-2xl sm:text-3xl text-brand-navy">{active.name}</h2>
          {active.intro && <p className="mt-1.5 text-brand-navy/75 leading-relaxed">{active.intro}</p>}
        </div>

        {active.checklist?.length > 0 && (
          <Checklist slug={slug} mode={mode} phaseKey={active.key} items={active.checklist} />
        )}

        {active.diagram && <SetupDiagram diagram={active.diagram} />}

        {active.cards?.length > 0 && (
          <div className="space-y-3">
            {active.cards.map((c) => (
              <StepCard key={c.number} {...c} />
            ))}
          </div>
        )}

        {active.tips?.length > 0 && (
          <div className="space-y-3">
            {active.tips.map((t, i) => (
              <Callout key={i}>{t}</Callout>
            ))}
          </div>
        )}

        {active.promotionTemplates?.length > 0 && (
          <PromotionTemplates templates={active.promotionTemplates} />
        )}
      </div>
    </ActivityShell>
  );
}
